import { z } from 'zod';
import { userWireSchema } from './user';

/**
 * Wire schemas for friendship API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 */

/**
 * Friend request status enum
 */
export const friendRequestStatusSchema = z.enum(['pending', 'accepted', 'rejected']);

/**
 * Friend request wire schema (matches backend response format)
 * Based on FriendRequestReadSchema from Gateway
 */
export const friendRequestWireSchema = z.object({
  id: z.string(),
  requester_id: z.string(),
  receiver_id: z.string(),
  status: friendRequestStatusSchema,
  created_at: z.string(), // D1 DATETIME (ISO format)
  updated_at: z.string(), // D1 DATETIME (ISO format)
  // Joined user data (when included)
  requester: userWireSchema.optional(),
  receiver: userWireSchema.optional(),
});

export type FriendRequestWire = z.infer<typeof friendRequestWireSchema>;

/**
 * Friendship wire schema (matches backend response format)
 * Based on FriendshipReadSchema from Gateway
 */
export const friendshipWireSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  friend_id: z.string(),
  created_at: z.string(), // D1 DATETIME (ISO format)
  // Joined friend user data
  friend: userWireSchema.optional(),
  // Optional enrichment stats
  friend_count: z.number().int().min(0).optional(),
  mutual_friend_count: z.number().int().min(0).optional(),
  check_in_count: z.number().int().min(0).optional(),
});

export type FriendshipWire = z.infer<typeof friendshipWireSchema>;

/**
 * Friendship status response schema
 * Response from GET /api/friends/status/{user_id}
 */
export const friendshipStatusWireSchema = z.object({
  status: z.enum(['none', 'pending_sent', 'pending_received', 'friends']),
  request_id: z.string().nullable().optional(),
});

export type FriendshipStatusWire = z.infer<typeof friendshipStatusWireSchema>;

/**
 * Paginated friend requests response schema
 */
export const paginatedFriendRequestsWireSchema = z.object({
  items: z.array(friendRequestWireSchema),
  total: z.number().int(),
  page: z.number().int(),
  per_page: z.number().int(),
  total_pages: z.number().int(),
});

export type PaginatedFriendRequestsWire = z.infer<typeof paginatedFriendRequestsWireSchema>;

/**
 * Paginated friendships response schema
 */
export const paginatedFriendshipsWireSchema = z.object({
  items: z.array(friendshipWireSchema),
  total: z.number().int(),
  page: z.number().int(),
  per_page: z.number().int(),
  total_pages: z.number().int(),
});

export type PaginatedFriendshipsWire = z.infer<typeof paginatedFriendshipsWireSchema>;

/**
 * Suggested friend wire schema
 * Response from GET /api/friends/suggested
 */
export const suggestedFriendWireSchema = z.object({
  user_id: z.string(),
  username: z.string().nullable().optional(),
  display_username: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  mutual_friend_count: z.number().int(),
});

export type SuggestedFriendWire = z.infer<typeof suggestedFriendWireSchema>;
